import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth, useClerk } from '@clerk/clerk-react';
import type { SetStatePayload } from './tim-bridge';

type AuthPayload = Extract<SetStatePayload, { type: 'AUTH' }>;
type Options = Pick<AuthPayload, 'role' | 'platform' | 'runtime' | 'lang'> & {
  iframeReady: boolean;
  send: (payload: SetStatePayload) => void;
};

export function useTimAuthSync({ iframeReady, send, role, platform, runtime, lang }: Options) {
  const { getToken, userId, orgId } = useAuth();
  const clerk = useClerk();
  const [ready, setReady] = useState(false);
  const generation = useRef(0);
  const canAuthenticate = !!userId && (role !== 'brand' || !!orgId);

  const syncAuth = useCallback(async (): Promise<boolean> => {
    const currentGeneration = ++generation.current;
    if (!userId || (role === 'brand' && !orgId)) {
      setReady(false);
      send({ type: 'AUTH', token: '', role, platform, runtime, lang });
      return false;
    }
    const token = await (process.env.REACT_APP_ENV === 'production'
      ? getToken()
      : getToken({ template: 'testing-template' })).catch(() => null);
    // Ignore an older request or a result received after cleanup.
    if (currentGeneration !== generation.current || !token) return false;
    send({ type: 'AUTH', token, role, platform, runtime, lang });
    setReady(true);
    return true;
  }, [getToken, userId, orgId, send, role, platform, runtime, lang]);

  useEffect(() => {
    setReady(false);
    if (!iframeReady) return;
    void syncAuth();
    // This installed Clerk SDK exposes token changes through its resource listener.
    let previousToken: string | undefined;
    const unsubscribe = clerk.addListener(({ session }) => {
      const token = session?.lastActiveToken?.getRawString();
      if (token === previousToken) return;
      previousToken = token;
      void syncAuth();
    });
    const syncWhenVisible = () => {
      if (document.visibilityState === 'visible') void syncAuth();
    };
    document.addEventListener('visibilitychange', syncWhenVisible);
    window.addEventListener('online', syncWhenVisible);
    return () => {
      generation.current += 1;
      unsubscribe();
      document.removeEventListener('visibilitychange', syncWhenVisible);
      window.removeEventListener('online', syncWhenVisible);
    };
  }, [iframeReady, clerk, syncAuth]);

  return { ready: canAuthenticate && ready };
}
